/**
 * The retention ladder: the screens between the cancellation reason and the confirm screen
 * (owner's design, 19/10). In order, each shown only when it has something to offer:
 *
 * 1. guarantee — inside the 7-day window: the refund is guaranteed until the deadline, keep using.
 * 2. bonus — a bonus the customer lacks, given while they stay subscribed, with its video.
 * 3. upgrade — Basic: the Premium until the refund deadline (7 days outside the window).
 *    Premium: the Zenpages, or the Zenlinks for someone who has it, with its video.
 * 0. autopilot — "não uso" only, first: the team runs the customer's Instagram for them, by hand
 *    until the Piloto Automático plan exists, at the plan they already pay (owner, 21/10). Each
 *    "yes" goes to #zenpost-vendas, which is how the owner measures the interest.
 * 4. specialist — the team adjusts the profile and builds the post planner by a business day; the
 *    customer only says what they disliked. Hidden inside the window when that day falls after
 *    the refund deadline, so taking it never costs the refund.
 *
 * That is the order for most reasons. A reason the ladder can answer moves its answer first (owner,
 * 21/10, `LADDER_ORDER_BY_REASON`): bad content, no results, hard to use or broken → the
 * specialist, since a gift does not fix a complaint about quality; missing features → the upgrade.
 *
 * Every screen says the refund is still guaranteed and moves on with one tap ("Não, obrigado"):
 * the right of withdrawal (CDC art. 49) lets us persuade, not obstruct. Zenpost decides what each
 * customer can get (`retention_ladder` in the cancel-rescue context); a Zenpost without it, or
 * RETENTION_LADDER_ENABLED off, sends null and the dialog keeps the earlier flow.
 */

import type { CancellationReason } from "./cancellation_reasons.js";

export const RETENTION_LADDER_STEPS = ["guarantee", "bonus", "upgrade", "specialist", "autopilot"] as const;
export type RetentionLadderStep = (typeof RETENTION_LADDER_STEPS)[number];

/** What a step offered, as the audit records it. */
export type RetentionLadderOfferKind = "keep_using" | "bonus" | "premium_trial" | "product" | "specialist" | "autopilot";
export const RETENTION_LADDER_OFFER_KINDS = [
  "keep_using",
  "bonus",
  "premium_trial",
  "product",
  "specialist",
  "autopilot",
] as const satisfies readonly RetentionLadderOfferKind[];

/** The reasons the autopilot answers: someone with no time to post (owner, 21/10). */
export const AUTOPILOT_REASONS: readonly CancellationReason[] = ["not_using"];

/**
 * A feature Zenpost gives for as long as the customer stays subscribed (one gift per account);
 * `name` is Zenpost's admin label (English). zenpay adds what the page shows with it.
 */
export interface RetentionGiftOffer {
  key: string;
  name: string;
  /** Its list price a month (BRL), the "~~R$ 7/mês~~" the gift is worth; null when unknown. */
  price_monthly_cents?: number | null;
  /** What it sells for once, for a gift sold that way (the Bunker course); then no monthly price. */
  price_once_cents?: number | null;
  /** Where it lives in app.zenpost.ai, for "Abrir agora". */
  open_url?: string | null;
}

export interface RetentionLadderOffers {
  bonus: RetentionGiftOffer | null;
  /** Premium customers only: the Zenpages, or the Zenlinks. */
  product: RetentionGiftOffer | null;
  /** Everyone but Premium: the Premium-only features Zenpost would lend. */
  premium_trial: {
    keys: string[];
    max_days: number;
    /** What the Premium costs a month over the Basic (BRL). */
    price_monthly_cents?: number | null;
    open_url?: string | null;
  } | null;
  /** Within the pause after an offer: the specialist alone, nothing given (owner, 21/10). */
  support_only?: boolean;
}

/** A gift's monthly price when the order bump catalog has none (the Zenpages before PIT-56 put it on sale). */
export const RETENTION_GIFT_FALLBACK_PRICE_CENTS: Readonly<Record<string, number>> = {
  zenpages: 1000,
};

/**
 * What a gift sells for on its own, once (BRL), shown in place of a monthly price: the Bunker
 * course's price on bnkr.co (owner, 19/10).
 */
export const RETENTION_GIFT_ONE_TIME_PRICE_CENTS: Readonly<Record<string, number>> = {
  bunker_course: 39700,
};

/** Where each gift lives in app.zenpost.ai (zenpost-frontend's routes). */
export const RETENTION_GIFT_APP_PATHS: Readonly<Record<string, string>> = {
  zenlinks: "/more/bonus/zenlinks",
  zenpages: "/more/bonus/zenpages",
  logo_generator: "/more/bonus/logo",
  instagram_bio: "/more/bonus/bio",
  fixed_posts_generator: "/more/bonus/fixed-posts",
  bunker_course: "/more/bonus/bunker",
};
/** The Premium trial opens on a Reel, the thing it adds. */
export const PREMIUM_TRIAL_APP_PATH = "/create/reel";

/**
 * What the specialist's work would cost elsewhere a month (BRL), shown as its value; null shows
 * none. TODO(retention-ladder): the owner's number.
 */
export const SPECIALIST_REFERENCE_MONTHLY_CENTS: number | null = null;

/**
 * The presentation video of each giftable key (YouTube ids, from each bonus page on zenpost.ai and
 * bnkr.co; the Zenpages one from the owner, 21/10).
 */
export const RETENTION_VIDEO_IDS: Readonly<Record<string, string>> = {
  zenlinks: "-bmZaoCI4rk",
  zenpages: "wSOGoeONZRU",
  logo_generator: "OOiOmT5olbs",
  instagram_bio: "iy6aRPZAuyQ",
  fixed_posts_generator: "VnEvyRkDrEk",
  bunker_course: "ZWJmQRvFAbs",
};

/** The videos that are landscape; the others are Shorts, upright. */
const LANDSCAPE_VIDEO_KEYS: ReadonlySet<string> = new Set(["bunker_course"]);

export function retentionVideoIsLandscape(key: string): boolean {
  return LANDSCAPE_VIDEO_KEYS.has(key);
}

export function retentionVideoEmbedUrl(key: string): string | null {
  const id = RETENTION_VIDEO_IDS[key];
  return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1` : null;
}

/** Outside the refund window the Premium trial lasts this long. */
export const PREMIUM_TRIAL_OUTSIDE_WINDOW_DAYS = 7;

/** Until when the Premium is lent: the refund deadline inside the window, 7 days outside it. */
export function premiumTrialExpiresAt(now: Date, refundableUntil: Date | null): Date {
  if (refundableUntil && refundableUntil.getTime() > now.getTime()) return refundableUntil;
  return new Date(now.getTime() + PREMIUM_TRIAL_OUTSIDE_WINDOW_DAYS * 24 * 3600 * 1000);
}

/** Brazil has had no daylight saving since 2019: São Paulo is UTC-3 all year. */
const SAO_PAULO_OFFSET_MS = -3 * 3600 * 1000;
/** The hour, in São Paulo, by which the specialist delivers. */
export const SPECIALIST_DELIVERY_HOUR = 18;

/**
 * When the specialist promises the planner: 18h (São Paulo) of the next business day. There is
 * no weekend shift, so Friday, Saturday and Sunday all deliver on Monday. Holidays are not known.
 */
export function specialistDeliverBy(now: Date): Date {
  const local = new Date(now.getTime() + SAO_PAULO_OFFSET_MS);
  const weekday = local.getUTCDay(); // 0 Sunday … 6 Saturday, in São Paulo
  const daysAhead = weekday === 5 ? 3 : weekday === 6 ? 2 : 1;
  const deliverLocal = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    local.getUTCDate() + daysAhead,
    SPECIALIST_DELIVERY_HOUR,
  );
  return new Date(deliverLocal - SAO_PAULO_OFFSET_MS);
}

/** Whether the specialist can deliver before the refund deadline (always, outside the window). */
export function specialistFitsRefundWindow(now: Date, refundableUntil: Date | null): boolean {
  if (!refundableUntil) return true;
  return specialistDeliverBy(now).getTime() <= refundableUntil.getTime();
}

/** The upgrade step offers the Premium trial or the product, whichever Zenpost sent. */
export function upgradeOfferKind(offers: RetentionLadderOffers): "premium_trial" | "product" | null {
  if (offers.premium_trial) return "premium_trial";
  if (offers.product) return "product";
  return null;
}

/** The step a reason's answer is: shown first, the others keep their order. */
const LADDER_ORDER_BY_REASON: Partial<Record<CancellationReason, RetentionLadderStep>> = {
  bad_content: "specialist",
  no_results: "specialist",
  hard_to_use: "specialist",
  technical_issues: "specialist",
  missing_features: "upgrade",
};

/** The ladder's screens for this customer, in order; empty when the ladder is off. */
export function retentionLadderSteps(args: {
  offers: RetentionLadderOffers | null;
  refundableUntil: Date | null;
  now: Date;
  reason?: CancellationReason | null;
}): RetentionLadderStep[] {
  if (!args.offers) return [];
  const inWindow = args.refundableUntil !== null && args.refundableUntil.getTime() > args.now.getTime();
  const specialistFits = specialistFitsRefundWindow(args.now, inWindow ? args.refundableUntil : null);
  if (args.offers.support_only) return specialistFits ? ["specialist"] : [];
  const steps: RetentionLadderStep[] = [];
  if (inWindow) steps.push("guarantee");
  if (args.offers.bonus) steps.push("bonus");
  if (upgradeOfferKind(args.offers)) steps.push("upgrade");
  if (specialistFits) steps.push("specialist");
  if (args.reason && AUTOPILOT_REASONS.includes(args.reason)) steps.unshift("autopilot");
  const first = args.reason ? LADDER_ORDER_BY_REASON[args.reason] : undefined;
  if (!first || !steps.includes(first)) return steps;
  return [first, ...steps.filter((s) => s !== first)];
}

/** Longest "what did you dislike" text kept; Slack and the audit both carry it. */
export const SPECIALIST_NOTES_MAX = 1000;
